import { useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import { baseOptions, seriesColors } from './setup.js';
import './Chart.css';

/*
 * Replaces the `scatterchartdir` directive.
 *
 * The original options object (directives.js:225-248) mixed Chart.js v2
 * syntax (`xAxes`, `yAxes`, `tooltips`) with v3+ syntax (`scales.x`,
 * `scales.y`) in the same object, so roughly half of it was being ignored
 * at runtime — including the y-axis bounds, which were also set to
 * `min: 0, max: 0`. Rebuilt here on v4 syntax: a single horizontal band of
 * points across the 0-100 percentile axis, with the y-axis hidden since it
 * carries no information.
 */
export default function ScatterChart({ title, label, values = [], height = 200 }) {
	/*
	 * The metric is a percentage of the time between two deadlines, so only
	 * 0-100 is meaningful. helper.py currently emits values from about -327
	 * to 37,000 for every manager sampled, so out-of-range points are
	 * dropped and counted rather than clipped into a misleading cluster or
	 * allowed to flatten the axis.
	 */
	const inRange = values.filter((v) => typeof v === 'number' && v >= 0 && v <= 100);
	const excluded = values.length - inRange.length;

	const { data, options } = useMemo(() => {
		const color = seriesColors()[0];
		const opts = baseOptions();

		opts.interaction = { mode: 'nearest', intersect: true };
		opts.plugins.legend.display = false;
		opts.plugins.tooltip = {
			...opts.plugins.tooltip,
			displayColors: false,
			callbacks: { label: (ctx) => `${label}: ${Math.round(ctx.parsed.x)}%` },
		};

		opts.scales = {
			x: {
				...opts.scales.x,
				type: 'linear',
				min: 0,
				max: 100,
				ticks: { ...opts.scales.x.ticks, stepSize: 10, callback: (v) => `${v}%` },
				title: {
					display: true,
					text: 'Percentile of time between deadlines',
					color: opts.scales.x.ticks.color,
				},
			},
			y: { display: false, min: -1, max: 1 },
		};

		return {
			data: {
				datasets: [
					{
						label,
						data: inRange.map((x) => ({ x, y: 0 })),
						backgroundColor: `${color}33`,
						borderColor: color,
						borderWidth: 1.5,
						radius: 10,
						hoverRadius: 14,
					},
				],
			},
			options: opts,
		};
	}, [label, inRange]);

	return (
		<figure className="chart">
			<figcaption className="chart__title">{title}</figcaption>
			<div className="chart__canvas" style={{ height }}>
				<Scatter data={data} options={options} />
			</div>
			{excluded > 0 && (
				<p className="chart__note">
					{excluded} of {values.length} transfers fell outside 0–100% and are not
					shown.
				</p>
			)}
		</figure>
	);
}
